import { Flex, Link, Stack, Text } from "@chakra-ui/react";
import { captureException } from "@sentry/core";
import { Fragment, type ReactElement, type ReactNode } from "react";
import { toast } from "react-toastify";

import type { Query } from "@/generated/graphql";

export type Lifecycle = "query" | "data-integrity" | "mutation" | "user-interaction" | "unknown";
type KeyValue = { [key: string]: unknown };

const TEXT_ERROR_MESSAGE =
  "This issue has been reported to our team, please try to refresh your page or contact us at momentum.support@virtuous.org.";

const DEFAULT_ERROR_MESSAGE = (
  <Text>
    This issue has been reported to our team.{" "}
    <Link onClick={() => window.location.reload()} fontWeight="bold" color="blue.500">
      Please try refreshing your page
    </Link>
    , or{" "}
    <Link
      href="mailto:momentum.support@virtuous.org?subject=In-App%20Support%20Request%3A%20%5Byour%20issue%20here%5D"
      isExternal
      color="blue.500"
    >
      submit a support request
    </Link>
    .
  </Text>
);
export type GraphqlTypename =
  | "UserType"
  | "CrmActionEmail"
  | "UserType.prospects"
  | NonNullable<NonNullable<Query["crmDonor"]>["__typename"]>
  | NonNullable<NonNullable<Query["recDrafted"]>["__typename"]>
  | NonNullable<NonNullable<Query["recLogged"]>["__typename"]>
  | NonNullable<NonNullable<Query["recTemplate"]>["__typename"]>
  | NonNullable<NonNullable<Query["rule"]>["__typename"]>
  | NonNullable<NonNullable<Query["recAuto"]>["__typename"] | "RecAutoType">
  | NonNullable<NonNullable<Query["recManual"]>["__typename"] | "RecManualType">
  | "SequenceTemplateType";

/**
 * @deprecated
 */
export function report(args: {
  error?: Error | unknown;
  userMessage?: string;
  context?: KeyValue;
  type?: Lifecycle;
  component?: string;
}): ReactElement | undefined {
  let errorFingerprint = TEXT_ERROR_MESSAGE;
  if (args.error instanceof Error) {
    errorFingerprint = args.error.message;
  } else if (!args.error) {
    errorFingerprint = "404 error";
  }
  captureException(args.error, { fingerprint: [errorFingerprint] });

  const errorMessage =
    args.userMessage ?? (args.error instanceof Error ? args.error.message : TEXT_ERROR_MESSAGE);

  if (args.userMessage) {
    toast.error(errorMessage, {
      position: "bottom-right",
      autoClose: 7_000,
      closeOnClick: true,
      draggable: false,
    });
    // not sure why we don't render a fallback here
    return;
  }
  // otherwise return a fallback component which can optionally be displayed to the user
  return <ComponentFallback name={args.component} context={<Text>{errorMessage}</Text>} />;
}

// Resetting the Apollo store cancels queries still in flight and refetches them straight
// after, so a cancellation is not a failure. Production builds carry only the error code
// (42 in @apollo/client 3.14) inside the docs link; dev builds may carry the text.
const STORE_RESET_CANCELLATION_MARKERS = [
  "Store reset while query was in flight",
  encodeURIComponent('"message":42,'),
];

function isStoreResetCancellation(error: unknown): boolean {
  return (
    error instanceof Error &&
    STORE_RESET_CANCELLATION_MARKERS.some((marker) => error.message.includes(marker))
  );
}

export function reportError(args: {
  error: Error | unknown;
  toastMsg?: string;
  type?: GraphqlTypename;
  isShowFeedbackPopup?: boolean;
  isShowToast?: boolean;
}): ReactElement {
  if (isStoreResetCancellation(args.error)) {
    return <Fragment />;
  }
  let errorMsg = args.error instanceof Error ? args.error.message : "Error unknown";
  if (errorMsg.includes("__all__")) {
    const jsonString = errorMsg.replaceAll("'", '"');
    const parsed = JSON.parse(jsonString) as { __all__?: string[] } | null;
    errorMsg = parsed?.__all__?.[0] ?? errorMsg;
  }
  captureException(args.error, { fingerprint: [errorMsg] });
  if (args.isShowToast ?? true) {
    toast.error(args.toastMsg ?? errorMsg, {
      position: "bottom-right",
      autoClose: 7_000,
      closeOnClick: true,
      draggable: false,
    });
  }
  return <ComponentFallback name={args.type} />;
}

export function reportErrorOr404(args: {
  error?: Error | unknown;
  type: GraphqlTypename;
  isShowToast?: boolean;
  toastMsg?: string;
}): ReactElement {
  if (isStoreResetCancellation(args.error)) {
    return <Fragment />;
  }
  const error = args.error ?? new MissingDataError(args.type);
  const errorMsg = error instanceof Error ? error.message : "Error unknown";
  captureException(error, { fingerprint: [errorMsg, args.type ?? ""], extra: { type: args.type } });
  if (args.isShowToast ?? true) {
    toast.error(args.toastMsg ?? errorMsg, {
      position: "bottom-right",
      autoClose: 7_000,
      closeOnClick: true,
      draggable: false,
    });
  }
  return <ComponentFallback name={args.type} />;
}

export class MissingDataError extends Error {
  constructor(public typename?: GraphqlTypename) {
    super(typename ? `No ${typename} returned` : "No data returned");
    this.name = "MissingDataError"; // sentry types by name, not constructor.name
  }
}

export const ComponentFallback = ({ name = "Component", context = DEFAULT_ERROR_MESSAGE }) => {
  const displayText =
    name === "CrmDonorType" ? "Unable to load donor information" : `Error loading ${name}`;

  return (
    <Stack key={name} h="100%" w="100%" p={4}>
      <Flex alignItems="center">
        <Text fontWeight="bold">{displayText}</Text>
      </Flex>
      {context}
    </Stack>
  );
};

export function success(message: string | ReactNode) {
  toast.success(message, {
    position: "bottom-right",
    autoClose: 4000,
    closeOnClick: false,
    draggable: false,
  });
}
